import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaEllipsisH,
  FaPencilAlt,
  FaTimes,
  FaPlus,
  FaUser,
  FaHistory,
  FaBolt,
  FaMobileAlt,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaTrash,
  FaDownload,
  FaTag,
  FaBarcode,
  FaPhone,
  FaUserMinus,
  FaUserPlus,
  FaPrint,
  FaCopy
} from 'react-icons/fa';
import { Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import TableView from './TableView';
import AssignSmartphoneModal from './AssignSmartphoneModal';
import EmployeeModal from './EmployeeModal';
import TimelineView from './TimelineView';
import AssignmentTimeline from './AssignmentTimeline';
import EquipmentLifeIndicator from './EquipmentLifeIndicator';
import InventoryAISummary from './InventoryAISummary';
import AddSmartphoneModal from './AddSmartphoneModal';
import SmartphoneModal from './SmartphoneModal';
import DeleteSmartphoneModal from './DeleteSmartphoneModal';
import GlobalFilter from './GlobalFilter';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import BrandLogo from './BrandLogo';
import Modal from './common/Modal';
import { QuickActionsMenu } from './common';
import axiosInstance from '../utils/axiosConfig';
import { getApiError, getBlobApiError } from '../utils/apiError';
import './AssetsView.css';

function SmartphonesView() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectedSmartphone, setSelectedSmartphone] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({
    id: false,
    marca: true,
    modelo: true,
    serial: true,
    imei: true,
    imei2: true,
    estado: true,
    empleado: false,
    empleado_linea: true,
    fecha_asignacion: false,
    activo_fijo: true,
    vida_util: false,
    acciones: true
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showIndividualTimeline, setShowIndividualTimeline] = useState(false);
  const [selectedSmartphoneForTimeline, setSelectedSmartphoneForTimeline] = useState(null);
  const [showSmartphoneModal, setShowSmartphoneModal] = useState(false);
  const [selectedSmartphoneForView, setSelectedSmartphoneForView] = useState(null);
  const [showConstanciaModal, setShowConstanciaModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignmentTimeline, setShowAssignmentTimeline] = useState(false);
  const [selectedForAssignments, setSelectedForAssignments] = useState(null);
  const [showAISummary, setShowAISummary] = useState(false);
  const [filteredCount, setFilteredCount] = useState(0);
  const [filteredRows, setFilteredRows] = useState([]);
  const [unassigning, setUnassigning] = useState(false);
  const [motivoDesasignar, setMotivoDesasignar] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const availableFilters = [
    {
      id: 'modelo',
      placeholder: 'Todos los modelos'
    },
    {
      id: 'estado',
      placeholder: 'Todos los estados'
    }
  ];

  useEffect(() => {
    // Defer data fetching to allow page transition animations to finish smoothly
    const timer = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/smartphones');
      setData(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(`Error cargando datos: ${error.message}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (id, field, value) => {
    try {
      console.log('handleSave called:', { id, field, value });
      const response = await axiosInstance.patch(`/api/smartphones/${id}`, { [field]: value });
      console.log('API Response:', response.data);

      if (response.data.message) {
        console.log('Updating state with:', { field, value });
        setData(old =>
          old.map(row =>
            row.id === id
              ? { ...row, [field]: value }
              : row
          )
        );
        return true;
      }
      console.log('Update not successful');
      return false;
    } catch (error) {
      console.error('Error in handleSave:', error);
      toast.error(getApiError(error, 'No se pudo guardar el cambio'));
      return false;
    }
  };

  const handleAssignClick = (smartphone) => {
    setSelectedSmartphone(smartphone);
    setShowAssignModal(true);
  };

  const handleAssignSuccess = () => {
    setShowAssignModal(false);
    setSelectedSmartphone(null);
    fetchData();
  };

  const handleUnassignClick = (smartphone) => {
    setSelectedSmartphone(smartphone);
    setMotivoDesasignar('');
    setShowConfirmModal(true);
  };

  const handleUnassign = async () => {
    if (unassigning) return;
    setUnassigning(true);
    try {
      await axiosInstance.post(`/api/smartphones/${selectedSmartphone.id}/desasignar`, {
        motivo: motivoDesasignar.trim()
      });
      // Refrescar datos desde el servidor para asegurar consistencia
      await fetchData();
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error al desasignar:', error);
      alert(getApiError(error, 'Error al desasignar el smartphone'));
    } finally {
      setUnassigning(false);
    }
  };

  const handleEmployeeClick = async (empleadoId) => {
    try {
      const empleadoResponse = await axiosInstance.get(`/api/empleados/${empleadoId}`);
      setSelectedEmployee(empleadoResponse.data);
      setShowEmployeeModal(true);
    } catch (error) {
      console.error('Error al obtener datos del empleado:', error);
    }
  };

  const handleAddSmartphone = (newSmartphone) => {
    setData(prevData => [...prevData, newSmartphone]);
  };

  const handleView = (smartphone) => {
    setSelectedSmartphoneForView(smartphone);
    setShowSmartphoneModal(true);
  };

  const handleSmartphoneUpdate = (updatedSmartphone) => {
    setData(prevData => prevData.map(item =>
      item.id === updatedSmartphone.id ? updatedSmartphone : item
    ));
  };

  const handleCopyInfo = (smartphone) => {
    const info = `
Marca: ${smartphone.marca || 'N/A'}
Modelo: ${smartphone.modelo || 'N/A'}
Serial: ${smartphone.serial || 'N/A'}
Activo Fijo: ${smartphone.activo_fijo || 'N/A'}
IMEI: ${smartphone.imei || 'N/A'}
IMEI2: ${smartphone.imei2 || 'N/A'}
Línea: ${smartphone.linea || 'N/A'}
Estado: ${smartphone.estado || 'N/A'}
${smartphone.empleado ? `Asignado a: ${smartphone.empleado}` : 'Sin asignar'}
    `.trim();

    try {
      // Crear un elemento textarea temporal
      const el = document.createElement('textarea');
      el.value = info;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);

      // Seleccionar y copiar el texto
      el.select();
      document.execCommand('copy');

      // Eliminar el elemento temporal
      document.body.removeChild(el);

      // Mostrar mensaje de éxito
      alert('Información copiada al portapapeles');
    } catch (err) {
      console.error('Error al copiar:', err);
      alert('Error al copiar la información');
    }
  };

  const handleDelete = (smartphone) => {
    setSelectedSmartphone(smartphone);
    setShowDeleteModal(true);
  };

  const handleDeleteSuccess = () => {
    setShowDeleteModal(false);
    setSelectedSmartphone(null);
    fetchData();
  };

  const EditableCell = ({ value, column, row, onSave, isBadgeType = false, isPhoneType = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);

    useEffect(() => {
      console.log('Value changed:', { value, currentValue, column: column.id });
      setCurrentValue(value);
    }, [value]);

    const handleClick = (e) => {
      if ((isBadgeType || isPhoneType) && value) {
        // Copiar al portapapeles con fallback
        const copyToClipboard = (text) => {
          // Método moderno
          if (navigator.clipboard && navigator.clipboard.writeText) {
            return navigator.clipboard.writeText(text);
          }

          // Fallback para navegadores antiguos o contextos no seguros
          return new Promise((resolve, reject) => {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            try {
              document.execCommand('copy');
              document.body.removeChild(textarea);
              resolve();
            } catch (err) {
              document.body.removeChild(textarea);
              reject(err);
            }
          });
        };

        copyToClipboard(value).then(() => {
          // Mostrar feedback visual temporal
          const badge = e.currentTarget.closest('.badge-cell');
          if (badge) {
            badge.classList.add('copied');
            setTimeout(() => {
              badge.classList.remove('copied');
            }, 300);
          }
        }).catch(err => {
          console.error('Error al copiar:', err);
        });
      }
    };

    const handleDoubleClick = () => {
      console.log('Double click, starting edit mode');
      setIsEditing(true);
    };

    const handleInputChange = (e) => {
      console.log('Input value changed:', e.target.value);
      setCurrentValue(e.target.value);
    };

    const handleChange = async (e) => {
      const newValue = e.target.value;
      console.log('handleChange called:', { newValue, field: column.id });

      try {
        console.log('Attempting to save:', { id: row.original.id, field: column.id, value: newValue });
        const success = await onSave(row.original.id, column.id, newValue);
        console.log('Save result:', success);

        if (success) {
          setCurrentValue(newValue);
        } else {
          console.log('Save failed, reverting to:', value);
          setCurrentValue(value);
        }
      } catch (error) {
        console.error('Error in handleChange:', error);
        setCurrentValue(value);
      }
      setIsEditing(false);
    };

    const handleBlur = () => {
      console.log('Input/Select blurred');
      setIsEditing(false);
    };

    if (isEditing) {
      return (
        <input
          type="text"
          value={currentValue || ''}
          onChange={handleInputChange}
          onBlur={handleChange}
          autoFocus
          className="editable-cell-input"
        />
      );
    }

    return (
      <div
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={`editable-cell ${isBadgeType ? 'badge-type' : ''} ${isPhoneType ? 'phone-type' : ''}`}
        title={(isBadgeType || isPhoneType) ? `Click para copiar, doble click para editar` : 'Doble click para editar'}
      >
        {value || 'Sin asignar'}
      </div>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
      },
      {
        header: 'Activo Fijo',
        accessorKey: 'activo_fijo',
        cell: ({ row, column, table }) => (
          <div className="badge-cell activo-fijo-badge">
            <FaTag className="badge-icon" />
            <EditableCell
              value={row.original.activo_fijo}
              column={column}
              row={row}
              table={table}
              onSave={handleSave}
              isBadgeType={true}
            />
          </div>
        )
      },
      {
        header: 'Marca',
        accessorKey: 'marca',
        cell: ({ row, column, table }) => (
          <div className="brand-cell">
            <BrandLogo brand={row.original.marca} />
          </div>
        )
      },
      {
        header: 'Modelo',
        accessorKey: 'modelo',
        cell: ({ row, column, table }) => (
          <EditableCell
            value={row.original.modelo}
            column={column}
            row={row}
            table={table}
            onSave={handleSave}
          />
        )
      },
      {
        header: 'Serial',
        accessorKey: 'serial',
        cell: ({ row, column, table }) => (
          <div className="badge-cell serial-badge">
            <FaBarcode className="badge-icon" />
            <EditableCell
              value={row.original.serial}
              column={column}
              row={row}
              table={table}
              onSave={handleSave}
              isBadgeType={true}
            />
          </div>
        )
      },
      {
        header: 'IMEI',
        accessorKey: 'imei',
        cell: ({ row, column, table }) => (
          <EditableCell
            value={row.original.imei}
            column={column}
            row={row}
            table={table}
            onSave={handleSave}
          />
        )
      },
      {
        header: 'IMEI2',
        accessorKey: 'imei2',
        cell: ({ row, column, table }) => (
          <EditableCell
            value={row.original.imei2}
            column={column}
            row={row}
            table={table}
            onSave={handleSave}
          />
        )
      },
      {
        header: 'Estado',
        accessorKey: 'estado',
        cell: ({ row }) => {
          const estado = row.original.estado?.toLowerCase() || '';
          const empleado = row.original.empleado;
          const manual = row.original.asignacion_manual;
          const empleadoId = row.original.empleado_id;

          if (estado === 'asignado' && empleado) {
            return (
              <span
                className="employee-type"
                onClick={() => handleEmployeeClick(empleadoId)}
                title={empleado}
              >
                <FaUser className="employee-icon" />
                <span className="employee-name">{empleado}</span>
              </span>
            );
          }

          if (estado === 'asignado' && !empleado && manual) {
            return (
              <span className="manual-badge" title={manual}>
                <FaUser className="employee-icon" />
                {manual}
              </span>
            );
          }

          return (
            <span className={`estado-badge ${estado.replace(/\s+/g, '-')}`}>
              {row.original.estado}
            </span>
          );
        }
      },
      {
        header: 'Empleado',
        accessorKey: 'empleado',
        cell: ({ row }) => {
          const empleado = row.original.empleado || row.original.asignacion_manual;
          const empleadoId = row.original.empleado_id;

          if (!empleado) return <span className="no-employee">Sin asignar</span>;

          return (
            <div
              className="employee-type clickable"
              onClick={() => handleEmployeeClick(empleadoId)}
              style={{ cursor: 'pointer' }}
            >
              {row.original.empleado ? <FaUser className="employee-icon" /> : null}
              <span className="employee-name">{empleado}</span>
            </div>
          );
        }
      },
      {
        header: 'Línea de Empleado',
        accessorKey: 'empleado_linea',
        cell: ({ row, column, table }) => {
          const linea = row.original.empleado_linea;
          if (!linea) return <span className="no-employee">N/A</span>;

          return (
            <div className="linea-badge">
              <a
                href={`tel:${linea}`}
                className="linea-icon"
                title="Llamar"
                onClick={(e) => e.stopPropagation()}
              >
                <FaPhone />
              </a>
              <span className="linea-number">{linea}</span>
            </div>
          );
        }
      },
      {
        header: 'Fecha de Asignación',
        accessorKey: 'fecha_asignacion',
        cell: ({ row }) => {
          const fecha = row.original.fecha_asignacion;
          if (!fecha) return <span className="no-employee">Sin asignar</span>;
          return new Date(fecha).toLocaleDateString('es-ES');
        }
      },
      {
        header: 'Vida útil',
        id: 'vida_util',
        cell: ({ row }) => (
          <EquipmentLifeIndicator createdAt={row.original.created_at} compact />
        ),
      },
      {
        header: 'Acciones',
        id: 'acciones',
        cell: ({ row }) => (
          <div className={`action-buttons ${activeActionMenu === row.original.id ? 'menu-active' : ''}`}>
            <button
              className="action-button view-button"
              title="Ver detalles"
              onClick={() => handleView(row.original)}
            >
              <FaEllipsisH />
            </button>

            <button
              className="action-button history-button"
              onClick={() => {
                setSelectedForAssignments(row.original);
                setShowAssignmentTimeline(true);
              }}
              title="Historial de asignaciones"
            >
              <FaHistory />
            </button>

            <div className="quick-actions-container" style={{ display: 'flex', alignItems: 'center' }}>
              <button
                className={`icon-button ${activeActionMenu === row.original.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (activeActionMenu === row.original.id) {
                    setActiveActionMenu(null);
                    setMenuAnchorRect(null);
                  } else {
                    setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                    setActiveActionMenu(row.original.id);
                  }
                }}
                title="Acciones rápidas"
              >
                <FaBolt />
              </button>
              <AnimatePresence>
                {activeActionMenu === row.original.id && (
                  <QuickActionsMenu anchorRect={menuAnchorRect}>
                    {[
                      {
                        icon: (row.original.empleado || row.original.asignacion_manual) ? FaUserMinus : FaUserPlus,
                        label: (row.original.empleado || row.original.asignacion_manual) ? 'Desasignar smartphone' : 'Asignar smartphone',
                        color: (row.original.empleado || row.original.asignacion_manual) ? '#EF4444' : '#10B981',
                        onClick: () => {
                          (row.original.empleado || row.original.asignacion_manual) ? handleUnassignClick(row.original) : handleAssignClick(row.original);
                          setActiveActionMenu(null);
                        }
                      },
                      {
                        icon: FaPrint,
                        label: 'Imprimir constancia',
                        color: '#3B82F6',
                        onClick: () => {
                          if (row.original.empleado || row.original.asignacion_manual) {
                            setSelectedSmartphone(row.original);
                            setShowConstanciaModal(true);
                          }
                          setActiveActionMenu(null);
                        }
                      },
                      {
                        icon: FaCopy,
                        label: 'Copiar información',
                        color: '#F59E0B',
                        onClick: () => {
                          handleCopyInfo(row.original);
                          setActiveActionMenu(null);
                        }
                      }
                    ].map((action) => (
                      <button
                        key={action.label}
                        className="quick-action-item"
                        onClick={action.onClick}
                      >
                        <span className="action-icon" style={{ color: action.color }}>
                          <action.icon />
                        </span>
                        <span className="action-label">{action.label}</span>
                      </button>
                    ))}
                  </QuickActionsMenu>
                )}
              </AnimatePresence>
            </div>

            <button
              className="action-button delete-button"
              title="Cambiar estado"
              onClick={() => handleDelete(row.original)}
            >
              <FaTimes />
            </button>
          </div>
        ),
      }
    ],
    [activeActionMenu]
  );

  const handleExportToCSV = () => {
    try {
      // Preparamos los datos para CSV, solo usando las columnas visibles
      const visibleColumns = columns
        .filter(col => columnVisibility[col.accessorKey || col.id])
        .filter(col => col.id !== 'acciones') // Excluimos la columna de acciones
        .map(col => ({
          key: col.accessorKey || col.id,
          header: typeof col.header === 'string' ? col.header : col.accessorKey || col.id
        }));

      // Se exportan las filas que quedan tras la búsqueda y los filtros de la tabla
      const rows = filteredRows;

      // Agregar columna de comentarios si hay notas
      const exportColumns = [...visibleColumns];
      const hasNotas = rows.some(row => row.notas);
      if (hasNotas) {
        exportColumns.push({ key: 'notas', header: 'Comentarios' });
      }

      // Creamos el encabezado del CSV
      let csvContent = exportColumns.map(col => `"${col.header}"`).join(',') + '\n';

      // Añadimos los datos de cada fila
      rows.forEach(row => {
        const rowData = exportColumns.map(col => {
          // Aseguramos que los valores no rompan el formato CSV
          let value = row[col.key] !== null && row[col.key] !== undefined ? row[col.key] : '';

          // Si es la columna de estado y está asignado, mostrar el nombre del empleado
          if (col.key === 'estado' && row.estado?.toLowerCase() === 'asignado') {
            if (row.empleado) {
              value = row.empleado;
            } else if (row.asignacion_manual) {
              value = row.asignacion_manual;
            }
          }

          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvContent += rowData.join(',') + '\n';
      });

      // Creamos un objeto Blob con el contenido CSV. El BOM (\uFEFF) le indica
      // a Excel que el archivo es UTF-8; sin él, las tildes salen rotas
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });

      // Creamos un enlace para descargar el archivo
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'Smartphones.csv');
      link.style.visibility = 'hidden';

      // Añadimos el enlace al DOM, hacemos clic y lo eliminamos
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error al exportar a CSV:", error);
      alert("Error al exportar a CSV. Por favor, inténtelo de nuevo.");
    }
  };

  return (
    <div className="assets-view">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
        <h2 className="page-title">Smartphones ({filteredCount})</h2>
        <div className="flex gap-3 items-center">
          <button
            className="btn-secondary"
            onClick={() => setShowAISummary(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#7c3aed', borderColor: '#c4b5fd' }}
          >
            <Sparkles size={14} />
            Resumen IA
          </button>
          <button
            className="btn-secondary"
            onClick={() => setShowTimeline(true)}
          >
            <FaHistory />
            Historial
          </button>
          <button
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            <FaPlus /> Nuevo Smartphone
          </button>
        </div>
      </div>

      <TableView
        data={data}
        columns={columns}
        loading={loading}
        error={error}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        defaultPageSize={30}
        defaultSorting={[{ id: 'activo_fijo', desc: true }]}
        activeActionMenu={activeActionMenu}
        setActiveActionMenu={setActiveActionMenu}
        availableFilters={availableFilters}
        onFilteredCountChange={setFilteredCount}
        onFilteredRowsChange={setFilteredRows}
      />

      <div className="flex justify-end mt-4">
        <button
          className="btn-secondary"
          onClick={handleExportToCSV}
          disabled={loading || filteredRows.length === 0}
        >
          <FaDownload /> Exportar a Excel
        </button>
      </div>

      {showAssignModal && selectedSmartphone && (
        <AssignSmartphoneModal
          onClose={() => setShowAssignModal(false)}
          onAssign={handleAssignSuccess}
          smartphone={selectedSmartphone}
        />
      )}

      <Modal
        isOpen={showConfirmModal && !!selectedSmartphone}
        onClose={() => {
          setShowConfirmModal(false);
          setSelectedSmartphone(null);
        }}
        size="sm"
      >
        <Modal.Header onClose={() => {
          setShowConfirmModal(false);
          setSelectedSmartphone(null);
        }}>
          Confirmar Desasignación
        </Modal.Header>
        <Modal.Body>
          <p>¿Está seguro que desea desasignar el siguiente smartphone?</p>
          <div className="smartphone-info">
            <p><strong>Marca:</strong> {selectedSmartphone?.marca}</p>
            <p><strong>Modelo:</strong> {selectedSmartphone?.modelo}</p>
            <p><strong>Empleado:</strong> {selectedSmartphone?.empleado || selectedSmartphone?.asignacion_manual}</p>
          </div>
          <div className="mt-4">
            <label htmlFor="motivo-desasignar-smartphone" className="block text-sm font-medium text-gray-700 mb-1">
              Motivo (opcional)
            </label>
            <textarea
              id="motivo-desasignar-smartphone"
              value={motivoDesasignar}
              onChange={(e) => setMotivoDesasignar(e.target.value)}
              rows={2}
              placeholder="Por qué se retira el equipo"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y focus:ring-2 focus:ring-sura-blue focus:border-transparent"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => {
              setShowConfirmModal(false);
              setSelectedSmartphone(null);
            }}
            className="btn-secondary"
            disabled={unassigning}
          >
            Cancelar
          </button>
          <button
            onClick={handleUnassign}
            className="btn-primary"
            disabled={unassigning}
          >
            {unassigning ? 'Desasignando...' : 'Confirmar'}
          </button>
        </Modal.Footer>
      </Modal>

      {showEmployeeModal && selectedEmployee && (
        <EmployeeModal
          employee={selectedEmployee}
          onClose={() => {
            setShowEmployeeModal(false);
            setSelectedEmployee(null);
          }}
        />
      )}

      {showAddModal && (
        <AddSmartphoneModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddSmartphone}
        />
      )}

      {showTimeline && (
        <TimelineView
          categoria="smartphones"
          onClose={() => setShowTimeline(false)}
        />
      )}

      {showIndividualTimeline && selectedSmartphoneForTimeline && (
        <TimelineView
          categoria="smartphones"
          itemId={selectedSmartphoneForTimeline.id}
          itemName={`${selectedSmartphoneForTimeline.marca} ${selectedSmartphoneForTimeline.modelo}`}
          onClose={() => {
            setShowIndividualTimeline(false);
            setSelectedSmartphoneForTimeline(null);
          }}
        />
      )}

      {showSmartphoneModal && selectedSmartphoneForView && (
        <SmartphoneModal
          smartphone={selectedSmartphoneForView}
          onClose={() => {
            setShowSmartphoneModal(false);
            setSelectedSmartphoneForView(null);
          }}
          onUpdate={handleSmartphoneUpdate}
        />
      )}

      <Modal
        isOpen={showConstanciaModal && !!selectedSmartphone}
        onClose={() => {
          setShowConstanciaModal(false);
          setSelectedSmartphone(null);
        }}
        size="sm"
      >
        <Modal.Header onClose={() => {
          setShowConstanciaModal(false);
          setSelectedSmartphone(null);
        }}>
          Confirmar Impresión
        </Modal.Header>
        <Modal.Body>
          <p>¿Desea generar la constancia de entrega?</p>
          <div className="smartphone-info">
            <p><strong>Marca:</strong> {selectedSmartphone?.marca}</p>
            <p><strong>Modelo:</strong> {selectedSmartphone?.modelo}</p>
            <p><strong>Empleado:</strong> {selectedSmartphone?.empleado}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => {
              setShowConstanciaModal(false);
              setSelectedSmartphone(null);
            }}
            className="btn-secondary"
          >
            No Generar
          </button>
          <button
            onClick={async () => {
              if (generatingPdf) return;
              setGeneratingPdf(true);
              try {
                const constanciaResponse = await axiosInstance.get(`/api/smartphones/${selectedSmartphone.id}/constancia`, {
                  responseType: 'blob'
                });

                const blob = constanciaResponse.data;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `constancia_entrega_${selectedSmartphone.empleado}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (error) {
                console.error('Error generando constancia:', error);
                toast.error(await getBlobApiError(error, 'Error al generar la constancia'));
              } finally {
                setGeneratingPdf(false);
              }
              setShowConstanciaModal(false);
              setSelectedSmartphone(null);
            }}
            className="btn-primary"
            disabled={generatingPdf}
          >
            {generatingPdf ? 'Generando...' : 'Generar PDF'}
          </button>
        </Modal.Footer>
      </Modal>

      {showDeleteModal && selectedSmartphone && (
        <DeleteSmartphoneModal
          smartphone={selectedSmartphone}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {showAssignmentTimeline && selectedForAssignments && (
        <AssignmentTimeline
          tipo="smartphone"
          equipoId={selectedForAssignments.id}
          equipoNombre={`${selectedForAssignments.marca} ${selectedForAssignments.modelo}`}
          onClose={() => {
            setShowAssignmentTimeline(false);
            setSelectedForAssignments(null);
          }}
        />
      )}

      {showAISummary && (
        <InventoryAISummary
          tipo="smartphones"
          onClose={() => setShowAISummary(false)}
        />
      )}
    </div>
  );
}

export default SmartphonesView;